import { create } from "zustand";
import { Catalog } from "../../../domains/catalog/aggregates/Catalog";
import { ProdutoTipo } from "../../../Infrastructure/interfaces/PedidoMeep";
import { Product } from "../../../domains/catalog/aggregates/Product";
import { CatalogRepository } from "../../../Infrastructure/repositories/api/CatalogRepository";
import { ICardGroupDiscounts } from "domains/cashless/aggregates/ICardCashless";
import { toast as toastfy } from "react-toastify";
import { RefObject } from "react";
import { Store } from "domains/catalog/aggregates/Store";
import { SeccionedProduct } from "domains/catalog/aggregates/SeccionedProduct";
import { getGlobalAddLog } from "Infrastructure/repositories/appInsights/AppInsights";

const addLog = getGlobalAddLog();

interface CatalogState {
  suggestionProductIds: string[];
  catalogRaw: Catalog | null;/** Catálogo completo da API, sem filtro de restrições (usado como base para filtrar) */
  catalog: Catalog | null;
  catalogRestrictions: string[];
  groupDiscounts: ICardGroupDiscounts[];
  modalValues: {
    open: boolean;
    openWeighing?: boolean;
    ref?: RefObject<HTMLDivElement>;
    product?: Product;
  };
  openProductDetail: (
    _productId: string,
    _refItem?: RefObject<HTMLDivElement>
  ) => void;
  closeProductDetail: () => void;
  sync: (localId: string, eventId?: string) => void;
  forceSync: (eventId?: string) => Promise<void>;
  getConfigurationProduct: (productType: ProdutoTipo) => Product | null;
  handleRestrictions: (restrictions: string[]) => void;
  handleDiscounts: (discounts: ICardGroupDiscounts[]) => void;
}

const catalogRepository = CatalogRepository();

export const useCatalogStore = create<CatalogState>((set, get) => {
  //const filterCatalog = (catalog: Catalog | null) => {
  //  if (!catalog) return null;

  //  const { catalogRestrictions, groupDiscounts } = get();
  //  const restrictionsSet = new Set(catalogRestrictions);
  //  const discountByProductId = new Map(
  //    groupDiscounts.map((d: ICardGroupDiscounts) => [d.produtoId, d.desconto])
  //  );

  //  return {
  //    ...catalog,
  //    stores: catalog.stores.map((store) => ({
  //      ...store,
  //      seccionedProduct: store.seccionedProduct
  //        .map((section) => ({
  //          ...section,
  //          products: section.products
  //            .filter((product) => !restrictionsSet.has(product.id))
  //            .map((product) => {
  //              const discount = discountByProductId.get(product.id);
  //              return discount != null ? { ...product, discount } : product;
  //            }),
  //        }))
  //        .filter((section) => section.products.length > 0),
  //    })),
  //  };
  //};


  const filterCatalog = (
    catalog: Catalog | null,
    overrides?: {
      restrictions?: string[];
      groupDiscounts?: ICardGroupDiscounts[];
    }
  ) => {
    if (!catalog) return null;

    const catalogRestrictions = overrides?.restrictions ?? get().catalogRestrictions;
    const restrictionsSet = new Set(catalogRestrictions);
    const discounts = overrides?.groupDiscounts ?? get().groupDiscounts;
    const discountByProductId = new Map<string, number>(
      discounts.map((d: ICardGroupDiscounts) => [d.produtoId, d.desconto])
    );

    // Array vazio de restrições = mudança para "sem restrições", forçar atualização do catálogo
    let catalogChanged = catalogRestrictions.length === 0;

    const stores = catalog.stores.map(store => {
      let storeChanged = false;

      const seccionedProduct = store.seccionedProduct.reduce<SeccionedProduct[]>(
        (sectionsAcc, section) => {
          let sectionChanged = false;
          const products: Product[] = [];

          for (const product of section.products) {
            if (restrictionsSet.has(product.id)) {
              sectionChanged = true;
              continue;
            }

            const discount = discountByProductId.get(product.id);
            if (discount != null && product.discount !== discount) {
              products.push({ ...product, discount });
              sectionChanged = true;
            } else {
              products.push(product);
            }
          }

          if (products.length > 0) {
            if (sectionChanged) storeChanged = true;

            sectionsAcc.push(
              sectionChanged
                ? { ...section, products }
                : section
            );
          } else {
            storeChanged = true;
          }

          return sectionsAcc;
        },
        [] as SeccionedProduct[]
      );

      if (!storeChanged) return store;

      catalogChanged = true;
      return { ...store, seccionedProduct };
    });

    return catalogChanged ? { ...catalog, stores } : catalog;
  };



  const sync = async (localId: string, eventId?: string) => {
    try {
      const isChange = await catalogRepository.syncIfDiffAsync(localId);
      if (isChange) {
        const _catalogSync = await catalogRepository.syncAsync(eventId ?? undefined);
        if (_catalogSync) {
          set({
            catalogRaw: _catalogSync,
            catalog: filterCatalog(_catalogSync),
          });
        }
      }
    } catch (error) {
      addLog && addLog("Catalog", error);
    }
  };

  const forceSync = async (eventId?: string) => {
    const _catalogSync = await catalogRepository.syncAsync(eventId ?? undefined);
    if (_catalogSync) {
      set({
        catalogRaw: _catalogSync,
        catalog: filterCatalog(_catalogSync),
      });
    }
  };

  const getConfigurationProduct = (productType: ProdutoTipo) => {
    return (
      get().catalog?.configProducts.find(
        (configProduct: Product) => configProduct.type === productType
      ) ?? null
    );
  };

  const handleRestrictions = (restrictions: string[]) => {
    set({ catalogRestrictions: restrictions });
    // Filtrar sempre a partir do catálogo completo para que, ao limpar restrições, os produtos voltem
    const raw = get().catalogRaw ?? get().catalog;
    set({ catalog: filterCatalog(raw) });
  };

  const handleDiscounts = (discounts: ICardGroupDiscounts[]) => {
    set((state: CatalogState) => {
      const raw = state.catalogRaw ?? state.catalog;
      return {
        ...state,
        groupDiscounts: discounts,
        catalog: filterCatalog(raw, { groupDiscounts: discounts }),
      };
    });
  };

  const openProductDetail = (
    _productId: string,
    _refItem?: RefObject<HTMLDivElement>
  ) => {
    const _store = get().catalog?.stores.find((s: Store) =>
      s.seccionedProduct.some((sp) =>
        sp.products.some((product) => product.id === _productId)
      )
    );

    if (_store?.isClosed) {
      toastfy.error("Loja fechada");
    } else {
      const product = _store?.seccionedProduct
        .flatMap((sp: SeccionedProduct) => sp.products)
        .find((product: Product) => product.id === _productId);

      if (product) {
        if (product.type === ProdutoTipo.Kilo) {
          set({ modalValues: { open: false, openWeighing: true, product, ref: _refItem } });
        } else {
          set({ modalValues: { open: true, product, ref: _refItem } });
        }
      } else {
        toastfy.error("Produto não encontrado");
      }
    }
  };

  const closeProductDetail = () => {
    const { modalValues } = get();
    const store = get().catalog?.stores.find((s: Store) =>
      s.seccionedProduct.some((sp) =>
        sp.products.includes(modalValues.product!)
      )
    );

    if (store?.isClosed) {
      toastfy.error("Loja fechada");
    } else {
      set({ modalValues: { open: false, openWeighing: false } });
    }
  };

  catalogRepository.getAsync().then((catalog) => {
    if (catalog) {
      set({
        catalogRaw: catalog,
        catalog: filterCatalog(catalog),
        suggestionProductIds: catalog?.cartRecommendationByLocal ?? [],
      });
    }
  });

  return {
    suggestionProductIds: [],
    catalogRaw: null,
    catalog: null,
    catalogRestrictions: [],
    groupDiscounts: [],
    modalValues: { open: false },
    openProductDetail,
    closeProductDetail,
    sync,
    forceSync,
    getConfigurationProduct,
    handleRestrictions,
    handleDiscounts,
  };
});
